import type { Permission } from '@/modules/identity/application/ability';
import type { TenantFeatures } from '@/shared/types';
import { AlertTriangle, Ban, BarChart3, Book, Camera, ClipboardList, CreditCard, FileText, FolderKanban, Home, Key, LayoutDashboard, LayoutGrid, LogIn, MapPin, Megaphone, Package, Settings, Shield, Ticket, Truck, UserCog, Users, Wallet, type LucideIcon } from 'lucide-react';

export interface NavItem {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  permission: Permission;
  tenantRole?: string | string[];
  path: string;
  moduleKey?: keyof TenantFeatures;
}

export interface NavCategory {
  id: string;
  labelKey: string;
  icon: LucideIcon;
  items: NavItem[];
}

export const NAV_CATEGORIES: NavCategory[] = [
  {
    id: 'main',
    labelKey: 'menu.categories.main',
    icon: LayoutGrid,
    items: [
      { id: 'dashboard', labelKey: 'menu.dashboard', icon: LayoutDashboard, permission: { action: 'read', subject: 'Dashboard' }, path: '/' },
    ],
  },
  {
    id: 'community',
    labelKey: 'menu.categories.community',
    icon: Users,
    items: [
      { id: 'announcements', labelKey: 'menu.announcements', icon: Megaphone, permission: { action: 'read', subject: 'Announcement' }, path: '/comunicados', moduleKey: 'enableCommunication' },
      { id: 'polls', labelKey: 'menu.polls', icon: BarChart3, permission: { action: 'read', subject: 'Poll' }, path: '/encuestas', moduleKey: 'enablePolls' },
      { id: 'transparency', labelKey: 'menu.transparency', icon: FolderKanban, permission: { action: 'read', subject: 'TransparencyProject' }, path: '/transparencia', moduleKey: 'enableTransparency' },
      { id: 'documents', labelKey: 'menu.documents', icon: FileText, permission: { action: 'read', subject: 'Document' }, path: '/documentos', moduleKey: 'enableDocuments' },
      { id: 'incidents', labelKey: 'menu.incidents', icon: Ticket, permission: { action: 'read', subject: 'Ticket' }, tenantRole: 'ADMIN', path: '/incidentes', moduleKey: 'enableTickets' },
    ],
  },
  {
    id: 'finances',
    labelKey: 'menu.categories.finances',
    icon: Wallet,
    items: [
      { id: 'payments', labelKey: 'menu.payments', icon: CreditCard, permission: { action: 'read', subject: 'Payment' }, tenantRole: 'RESIDENT', path: '/pagos', moduleKey: 'enablePayments' },
      { id: 'cobranza', labelKey: 'menu.cobranza', icon: Ban, permission: { action: 'read', subject: 'Cobranza' }, path: '/cobranza' },
    ],
  },
  {
    id: 'security',
    labelKey: 'menu.categories.security',
    icon: Shield,
    items: [
      { id: 'cameras', labelKey: 'menu.cameras', icon: Camera, permission: { action: 'read', subject: 'Camera' }, path: '/camaras', moduleKey: 'enableCameras' },
      { id: 'access', labelKey: 'menu.access', icon: Key, permission: { action: 'read', subject: 'AccessControl' }, path: '/accesos', moduleKey: 'enableAccessControl' },
      { id: 'access-logs', labelKey: 'menu.accessLogs', icon: LogIn, permission: { action: 'read', subject: 'AccessLog' }, tenantRole: 'GUARD', path: '/registro-accesos', moduleKey: 'enableAccessControl' },
      { id: 'incidents', labelKey: 'menu.incidents', icon: Ticket, permission: { action: 'read', subject: 'Ticket' }, tenantRole: ['GUARD', 'RESIDENT', 'FAMILIAR'], path: '/incidentes', moduleKey: 'enableTickets' },
      { id: 'emergency', labelKey: 'menu.emergency', icon: AlertTriangle, permission: { action: 'read', subject: 'EmergencyAlert' }, tenantRole: 'GUARD', path: '/emergencias', moduleKey: 'enableEmergency' },
      { id: 'restrictions', labelKey: 'menu.restrictions', icon: Ban, permission: { action: 'read', subject: 'Restriction' }, tenantRole: 'GUARD', path: '/restricciones', moduleKey: 'enableRestrictions' },
    ],
  },
  {
    id: 'operations',
    labelKey: 'menu.categories.operations',
    icon: Settings,
    items: [
      { id: 'amenities', labelKey: 'menu.amenities', icon: Home, permission: { action: 'read', subject: 'Amenity' }, tenantRole: ['RESIDENT', 'FAMILIAR'], path: '/amenidades', moduleKey: 'enableAmenities' },
      { id: 'logbook', labelKey: 'menu.logbook', icon: ClipboardList, permission: { action: 'read', subject: 'Logbook' }, path: '/bitacora', moduleKey: 'enableLogbook' },
      { id: 'patrol', labelKey: 'menu.patrol', icon: MapPin, permission: { action: 'read', subject: 'PatrolRound' }, tenantRole: 'GUARD', path: '/rondines', moduleKey: 'enablePatrol' },
      { id: 'directory', labelKey: 'menu.directory', icon: Book, permission: { action: 'read', subject: 'GuardPanel' }, tenantRole: 'GUARD', path: '/directorio', moduleKey: 'enableDirectory' },
      { id: 'suppliers', labelKey: 'menu.suppliers', icon: Truck, permission: { action: 'read', subject: 'Supplier' }, tenantRole: 'GUARD', path: '/proveedores', moduleKey: 'enableSuppliers' },
      { id: 'inventory', labelKey: 'menu.inventory', icon: Package, permission: { action: 'read', subject: 'Asset' }, path: '/inventario', moduleKey: 'enableInventory' },
      { id: 'reports', labelKey: 'menu.reports', icon: ClipboardList, permission: { action: 'read', subject: 'Report' }, path: '/reportes', moduleKey: 'enableReports' },
    ],
  },
  {
    id: 'admin',
    labelKey: 'menu.categories.admin',
    icon: UserCog,
    items: [
      { id: 'admin', labelKey: 'menu.admin', icon: Users, permission: { action: 'manage', subject: 'all' }, path: '/admin' },
    ],
  },
];

// Flatten categories for backward compatibility
export const NAV_ITEMS: NavItem[] = NAV_CATEGORIES.flatMap(cat => cat.items);

export const ROUTE_PERMISSIONS: Record<string, Permission> = {
  ...Object.fromEntries(NAV_ITEMS.map(item => [item.id, item.permission])),
  profile: { action: 'read', subject: 'Profile' },
  notifications: { action: 'read', subject: 'Notification' },
};

// Map paths to permissions for route protection
export const PATH_PERMISSIONS: Record<string, Permission> = {
  ...Object.fromEntries(NAV_ITEMS.map(item => [item.path, item.permission])),
  '/perfil': { action: 'read', subject: 'Profile' },
  '/notificaciones': { action: 'read', subject: 'Notification' },
};

// Map paths to nav item IDs
export const PATH_TO_ID: Record<string, string> = {
  ...Object.fromEntries(NAV_ITEMS.map(item => [item.path, item.id])),
  '/perfil': 'profile',
  '/notificaciones': 'notifications',
};
